import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { FiTrash2, FiUser, FiCalendar, FiMoreVertical, FiEdit2, FiX, FiAlertTriangle } from "react-icons/fi";
import { updateTask } from "../../services/connections";
import "../../styles/TaskItem.css";
import "../../styles/CreateTaskForm.css";

export default function TaskItem({ task, onDelete, onDone, onArchive, onTaskUpdated }) {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const menuRef = useRef(null);

  // Helper function to format date for input (YYYY-MM-DD format)
  const formatDateForInput = (date) => {
    if (!date) return "";

    // If it's a Firestore Timestamp
    if (date?.toDate) {
      const d = date.toDate();
      return d.toISOString().split('T')[0];
    }

    // If it's a Date object
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }

    // If it's a string in dd/mm/yyyy format
    if (typeof date === 'string' && date.includes('/')) {
      const parts = date.split('/');
      if (parts.length === 3) {
        // Convert dd/mm/yyyy to yyyy-mm-dd
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }

    // If it's already in yyyy-mm-dd format
    if (typeof date === 'string' && date.includes('-')) {
      return date;
    }

    return "";
  };

  // Edit form state
  const [editTitle, setEditTitle] = useState(task.title);
  const [editAssignedTo, setEditAssignedTo] = useState(task.assignedTo);
  const [editDescription, setEditDescription] = useState(task.description);
  const [editDueDate, setEditDueDate] = useState(formatDateForInput(task.dueDate));
  const [editPriority, setEditPriority] = useState(task.priority || "Medium");
  const [editError, setEditError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close modals on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setShowEditModal(false);
        setShowDeleteConfirm(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  const openEditModal = () => {
    setEditTitle(task.title);
    setEditAssignedTo(task.assignedTo);
    setEditDescription(task.description);
    setEditDueDate(formatDateForInput(task.dueDate));
    setEditPriority(task.priority || "Medium");
    setEditError("");
    setShowEditModal(true);
    setShowMenu(false);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError("");

    if (!editTitle || !editAssignedTo || !editDescription || !editDueDate) {
      setEditError("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      await updateTask(task.id, {
        title: editTitle,
        assignedTo: editAssignedTo,
        description: editDescription,
        dueDate: editDueDate,
        priority: editPriority,
      });

      setShowEditModal(false);
      if (onTaskUpdated) onTaskUpdated();
    } catch (error) {
      setEditError("Failed to update task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = () => {
    onDelete(task.id);
    setShowDeleteConfirm(false);
  };

  const getBorderColor = () => {
    if (task.status === "Done" || task.status === "archived") {
      return "#6b7280";
    }
    switch (task.priority) {
      case "High":
        return "#ef4444";
      case "Medium":
        return "#f59e0b";
      case "Low":
        return "#22c55e";
      default:
        return "#6b7280";
    }
  };

  return (
    <div
      className={`task-card ${task.status === "Done" || task.status === "archived" ? "completed" : ""}`}
      style={{ border: `3px solid ${getBorderColor()}` }}
      data-testid="task-card"
    >
      {task.status !== "archived" && (
        <div className="menu-container" ref={menuRef} style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 100 }}>
          <button
            type="button"
            className="menu-btn"
            data-testid="menu-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            style={{ background: 'transparent', border: 'none', padding: '6px', cursor: 'pointer', color: '#6b7280' }}
          >
            <FiMoreVertical />
          </button>
          {showMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '0',
              marginTop: '4px',
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
              minWidth: '140px',
              zIndex: 9999,
              overflow: 'hidden'
            }}>
              <button
                type="button"
                className="dropdown-edit-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'white',
                  border: 'none',
                  borderBottom: '1px solid #f3f4f6',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  transition: 'background 0.15s',
                  WebkitTapHighlightColor: 'rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                onTouchStart={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onTouchEnd={(e) => e.currentTarget.style.background = 'white'}
                onClick={openEditModal}
              >
                <FiEdit2 style={{ fontSize: '16px', pointerEvents: 'none' }} /> Edit
              </button>
              <button
                type="button"
                className="dropdown-delete-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#ef4444',
                  transition: 'background 0.15s',
                  WebkitTapHighlightColor: 'rgba(239,68,68,0.1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                onTouchStart={(e) => e.currentTarget.style.background = '#fef2f2'}
                onTouchEnd={(e) => e.currentTarget.style.background = 'white'}
                onClick={() => { setShowDeleteConfirm(true); setShowMenu(false); }}
              >
                <FiTrash2 style={{ fontSize: '16px', pointerEvents: 'none' }} /> Delete
              </button>
            </div>
          )}
        </div>
      )}

      <div className="task-content">
        <h5 className="task-title">{task.title}</h5>
        <p className="task-description">
          {showFullDescription
            ? task.description
            : task.description.length > 80
              ? `${task.description.slice(0, 80)}...`
              : task.description}
        </p>
        {task.description.length > 80 && (
          <button className="read-more-btn" onClick={toggleDescription}>
            {showFullDescription ? "Read less" : "Read more"}
          </button>
        )}
      </div>

      <div className="task-meta">
        <div className="meta-item">
          <FiUser className="meta-icon" />
          <span>{task.assignedTo}</span>
        </div>
        <div className="meta-item">
          <FiCalendar className="meta-icon" />
          <span>
            {task.dueDate?.toDate
              ? task.dueDate.toDate().toLocaleDateString()
              : task.dueDate}
          </span>
        </div>
      </div>

      {task.status === "archived" ? null : task.status !== "Done" ? (
        <button className="action-btn done-btn" data-testid="done-btn" onClick={() => onDone(task.id)}>
          ✓ Done
        </button>
      ) : (
        <button className="action-btn archive-btn" data-testid="archive-btn" onClick={() => onArchive(task.id)}>
          Archive
        </button>
      )}

      {/* Edit Modal - rendered via portal */}
      {showEditModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn-floating" onClick={() => setShowEditModal(false)}>
              <FiX />
            </button>
            <div className="edit-modal-inner">
              <h2 className="create-task-title">Edit Task</h2>
              <form onSubmit={handleEditSubmit} className="create-task-form">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    placeholder="Enter task title"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Assigned to</label>
                  <input
                    type="text"
                    placeholder="Enter employee name"
                    value={editAssignedTo}
                    onChange={(e) => setEditAssignedTo(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Describe the task"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    required
                  ></textarea>
                </div>

                <div className="priority-section">
                  <label>Priority</label>
                  <div className="priority-buttons">
                    <button
                      type="button"
                      className={`priority-btn high ${editPriority === "High" ? "active" : ""}`}
                      onClick={() => setEditPriority("High")}
                    >
                      High
                    </button>
                    <button
                      type="button"
                      className={`priority-btn medium ${editPriority === "Medium" ? "active" : ""}`}
                      onClick={() => setEditPriority("Medium")}
                    >
                      Medium
                    </button>
                    <button
                      type="button"
                      className={`priority-btn low ${editPriority === "Low" ? "active" : ""}`}
                      onClick={() => setEditPriority("Low")}
                    >
                      Low
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                    required
                  />
                </div>

                {editError && (
                  <div className="error-alert">
                    {editError}
                  </div>
                )}

                <div className="modal-actions" style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={() => setShowEditModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: '2px solid #9ca3af',
                      background: 'white',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      color: '#374151'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isSubmitting}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      background: '#22c55e',
                      color: 'white',
                      border: '2px solid #22c55e',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      cursor: 'pointer'
                    }}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal - rendered via portal */}
      {showDeleteConfirm && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <FiAlertTriangle />
            </div>
            <h3>Delete Task</h3>
            <p>Are you sure you want to delete "{task.title}"?</p>
            <p className="confirm-warning">This action cannot be undone.</p>
            <div className="confirm-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="delete-confirm-btn" onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}